<script setup>
import { ref, onMounted } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';

const showAlert = (message, action = null) => {
  useAlert(message, action);
};

const props = defineProps({
  currentPlan: {
    type: Object,
    default: null
  }
});

const emit = defineEmits(['planChanged']);

const store = useStore();

const isLoading = ref(true);
const plans = ref([]);
const selectedPlan = ref(null);
const isSubscribing = ref(false);
const localCurrentPlan = ref(null);

const fetchPlans = async () => {
  try {
    const data = await store.dispatch('accounts/getPlans');
    plans.value = data.plans;

    // Use current plan from props if provided
    if (props.currentPlan) {
      localCurrentPlan.value = props.currentPlan;
    }
  } catch (error) {
    showAlert('Failed to load plans');
  } finally {
    isLoading.value = false;
  }
};

const subscribeToPlan = async (plan) => {
  if (isSubscribing.value) return;

  isSubscribing.value = true;
  selectedPlan.value = plan;

  try {
    const data = await store.dispatch('accounts/subscribeToPlan', {
      planId: plan.id,
      priceId: plan.stripe_price_id,
    });
    if (data.success) {
      showAlert(`Successfully subscribed to ${plan.name}!`);
      localCurrentPlan.value = plan;

      // Emit plan change event to parent
      emit('planChanged', plan);
    }
  } catch (error) {
    showAlert(error.response?.data?.error || 'Failed to subscribe to plan');
  } finally {
    isSubscribing.value = false;
    selectedPlan.value = null;
  }
};

  // eslint-disable-next-line no-unused-vars
const formatPrice = (price) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(price / 100);
};

const formatFeatures = (features) => {
  if (!features) return [];
  
  const allFeatures = [
    ...(features.premium_features || []),
    ...(features.standard_features || [])
  ];
  
  return allFeatures;
};

const isPopular = (plan) => {
  return plan.metadata?.popular === true;
};

const getPopularBadge = (plan) => {
  if (isPopular(plan)) {
    return 'Most Popular';
  }
  return '';
};

const getPlanAction = (plan) => {
  if (localCurrentPlan.value?.id === plan.id) {
    return 'Current Plan';
  }
  
  if (plan.price === 0) {
    return 'Get Started Free';
  }
  
  if (!localCurrentPlan.value) {
    return `Subscribe to ${plan.name}`;
  }
  
  // Compare prices to determine upgrade/downgrade
  if (plan.price > localCurrentPlan.value.price) {
    return `Upgrade to ${plan.name}`;
  } else if (plan.price < localCurrentPlan.value.price) {
    return `Downgrade to ${plan.name}`;
  } else {
    return `Switch to ${plan.name}`;
  }
};

onMounted(() => {
  fetchPlans();
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <div class="bg-white dark:bg-slate-800 rounded-lg shadow-lg p-6">
    <div class="mb-8 text-center">
      <h2 class="text-3xl font-bold text-slate-900 dark:text-slate-100 mb-2">
        Flexible plans for growing teams
      </h2>
      <p class="text-slate-600 dark:text-slate-400 text-lg">
        Choose the perfect plan for your business needs
      </p>
    </div>

    <div v-if="isLoading" class="text-center py-12">
      <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      <p class="mt-2 text-slate-600 dark:text-slate-400">Loading plans...</p>
    </div>

    <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      <div
        v-for="plan in plans"
        :key="plan.id"
        class="relative rounded-lg border-2 p-6 transition-all hover:shadow-lg"
        :class="{
          'border-blue-500 bg-blue-50 dark:bg-blue-900/20 scale-105': localCurrentPlan?.id === plan.id,
          'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600': localCurrentPlan?.id !== plan.id,
          'border-orange-500 bg-orange-50 dark:bg-orange-900/20': isPopular(plan) && localCurrentPlan?.id !== plan.id
        }"
      >
        <!-- Popular Badge -->
        <div v-if="isPopular(plan) && localCurrentPlan?.id !== plan.id" class="absolute -top-3 left-1/2 transform -translate-x-1/2">
          <span class="bg-orange-500 text-white text-xs px-3 py-1 rounded-full font-semibold">
            {{ getPopularBadge(plan) }}
          </span>
        </div>

        <!-- Current Plan Badge -->
        <div v-if="localCurrentPlan?.id === plan.id" class="absolute top-0 right-0 bg-blue-500 text-white text-xs px-2 py-1 rounded-bl-lg rounded-tr-lg">
          Current Plan
        </div>

        <!-- Plan Header -->
        <div class="mb-6 text-center">
          <h3 class="text-xl font-bold text-slate-900 dark:text-slate-100 mb-2">
            {{ plan.name }}
          </h3>
          <p class="text-slate-600 dark:text-slate-400 text-sm mb-4">
            {{ plan.description }}
          </p>
          
          <!-- Pricing -->
          <div class="mb-2">
            <div class="text-3xl font-bold text-slate-900 dark:text-slate-100">
              <span v-if="plan.price === 0">FREE</span>
              <span v-else>
                ${{ plan.price / 100 }}
                <span class="text-sm font-normal text-slate-600 dark:text-slate-400">/agent/month</span>
              </span>
            </div>
            <div v-if="plan.yearly_price > 0" class="text-xs text-slate-500 dark:text-slate-400">
              ${{ plan.yearly_price / 100 }}/agent/year billed annually
            </div>
          </div>
        </div>

        <!-- Features -->
        <div class="mb-6">
          <h4 class="font-semibold text-slate-900 dark:text-slate-100 mb-3 text-sm">What's included:</h4>
          <ul class="space-y-2">
            <li
              v-for="feature in formatFeatures(plan.features)"
              :key="feature"
              class="flex items-start text-sm text-slate-600 dark:text-slate-400"
            >
              <svg class="w-4 h-4 mr-2 text-green-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
              </svg>
              <span>{{ feature }}</span>
            </li>
          </ul>
        </div>

        <!-- Captain AI Info -->
        <div v-if="plan.features?.captain_responses > 0" class="mb-4 p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
          <div class="text-xs text-slate-600 dark:text-slate-400">
            <div class="font-semibold text-slate-700 dark:text-slate-300 mb-1">Captain AI</div>
            <div>• {{ plan.features.captain_responses }} responses/month</div>
            <div v-if="plan.features.captain_documents > 0">• {{ plan.features.captain_documents }} documents/month</div>
          </div>
        </div>

        <!-- Subscribe Button -->
        <button
          @click="subscribeToPlan(plan)"
          :disabled="isSubscribing || localCurrentPlan?.id === plan.id"
          class="w-full py-3 px-4 rounded-lg font-medium transition-colors"
          :class="{
            'bg-blue-600 text-white hover:bg-blue-700': localCurrentPlan?.id !== plan.id && !isPopular(plan),
            'bg-orange-500 text-white hover:bg-orange-600': localCurrentPlan?.id !== plan.id && isPopular(plan),
            'bg-slate-300 text-slate-500 cursor-not-allowed': localCurrentPlan?.id === plan.id,
            'opacity-50 cursor-not-allowed': isSubscribing && selectedPlan?.id === plan.id
          }"
        >
          <span v-if="isSubscribing && selectedPlan?.id === plan.id">
            <div class="flex items-center justify-center">
              <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Subscribing...
            </div>
          </span>
          <span v-else>
            {{ getPlanAction(plan) }}
          </span>
        </button>
      </div>
    </div>

    <!-- Additional Info -->
    <div class="mt-12 text-center">
      <div class="bg-slate-50 dark:bg-slate-700 rounded-lg p-6">
        <h3 class="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">Questions?</h3>
        <p class="text-slate-600 dark:text-slate-400 mb-4">
          Need help choosing the right plan? We're here to help!
        </p>
        <div class="flex flex-col sm:flex-row gap-4 justify-center">
          <a href="#" class="text-blue-600 hover:text-blue-700 font-medium">Contact Sales</a>
          <a href="#" class="text-blue-600 hover:text-blue-700 font-medium">View Documentation</a>
          <a href="#" class="text-blue-600 hover:text-blue-700 font-medium">Schedule Demo</a>
        </div>
      </div>
    </div>
  </div>
  <!-- eslint-enable vue/no-bare-strings-in-template -->
</template>
